<script setup>
// NOTICE: KAPTION-INTEGRATION - Added auto-assign notice for Kaption AI conversations
import { computed } from 'vue';
// NOTICE: KAPTION-INTEGRATION
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';

import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { canUseFeature } = useFeaturePermissions();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const getInboxById = useMapGetter('inboxes/getInboxById');

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : 0;
    store.dispatch('setCurrentChatAssignee', agent);
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
    });
  },
});

const isUserTyping = computed(
  () => props.message !== '' && !props.isOnPrivateNote
);
const isUnassigned = computed(() => !assignedAgent.value);
const isAssignedToOtherAgent = computed(
  () => assignedAgent.value?.id !== currentUser.value?.id
);

// NOTICE: KAPTION-INTEGRATION - Check if assigned to Kaption AI assistant
const inbox = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  return inboxId ? getInboxById.value(inboxId) : null;
});

const isAssignedToKaptionAI = computed(() => {
  const assigneeEmail = assignedAgent.value?.email;
  return assigneeEmail?.endsWith('@assistants.kaptionai.com') ?? false;
});

const isAutoAssignOnReplyEnabled = computed(() => {
  // Default is true - only disabled if explicitly set to false
  const config = inbox.value?.auto_assignment_config;
  return config?.kaption_auto_assign_on_human_reply !== false;
});

// NOTICE: KAPTION-INTEGRATION - Show auto-assign banner when:
// 1. User is typing a message (not a private note)
// 2. Conversation is assigned to Kaption AI
// 3. Auto-assign on reply is enabled
const showAutoAssignBanner = computed(() => {
  return (
    canUseFeature('direct_assignment') &&
    isUserTyping.value &&
    isAssignedToKaptionAI.value &&
    isAutoAssignOnReplyEnabled.value
  );
});

const showSelfAssignBanner = computed(() => {
  // Don't show self-assign banner if auto-assign banner will be shown
  if (showAutoAssignBanner.value || !canUseFeature('direct_assignment'))
    return false;
  return (
    isUserTyping.value && (isUnassigned.value || isAssignedToOtherAgent.value)
  );
});

const showBotHandoffBanner = computed(
  () =>
    isUserTyping.value &&
    currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const isMuted = computed(() => !!currentChat.value?.muted);

const mutedBannerMessage = computed(() => {
  const mutedUntil = currentChat.value?.muted_until;
  if (mutedUntil === 'until_resolved') {
    return t('CONVERSATION.MUTED_BANNER.MUTED_UNTIL_RESOLVED');
  }
  if (mutedUntil) {
    return `${t('CONVERSATION.MUTED_BANNER.MUTED_UNTIL')} ${snoozedReopenTime(mutedUntil)}`;
  }
  return t('CONVERSATION.MUTED_BANNER.MUTED_FOREVER');
});

const botHandoffActionLabel = computed(() => {
  return assignedAgent.value?.id === currentUser.value?.id
    ? t('CONVERSATION.BOT_HANDOFF_REOPEN_ACTION')
    : t('CONVERSATION.BOT_HANDOFF_ACTION');
});

const selfAssignConversation = async () => {
  if (!canUseFeature('direct_assignment')) return;
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: wootConstants.STATUS_TYPE.OPEN,
  });
};

const onClickBotHandoff = async () => {
  try {
    await reopenConversation();

    if (needsAssignmentToCurrentUser.value) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};

const onClickUnmute = async () => {
  try {
    await store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE_SUCCESS'));
  }
};
</script>

<template>
  <Banner
    v-if="isMuted"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="mutedBannerMessage"
    has-action-button
    :action-button-label="$t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE')"
    @primary-action="onClickUnmute"
  />
  <!-- NOTICE: KAPTION-INTEGRATION - Auto-assign notice for Kaption AI conversations -->
  <Banner
    v-if="showAutoAssignBanner && !showBotHandoffBanner"
    color-scheme="primary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.AUTO_ASSIGN_ON_REPLY')"
  />
  <Banner
    v-if="showSelfAssignBanner && !showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
    has-action-button
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <Banner
    v-if="showBotHandoffBanner && canUseFeature('direct_assignment')"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.BOT_HANDOFF_MESSAGE')"
    has-action-button
    :action-button-label="botHandoffActionLabel"
    @primary-action="onClickBotHandoff"
  />
</template>
